import { FileOpener } from "@src-capacitor/@capacitor-community/file-opener"
import { Filesystem } from "@src-capacitor/@capacitor/filesystem"
import axios, { CanceledError } from "axios"
import { Platform } from "quasar"
import { csiAPI } from "src/boot/axios"
import { i18n } from "src/boot/i18n"
import {
  AttachmentDataCollection,
  AttachmentDocument,
} from "src/db/collectionDefinitions/csi/attachment"
import { getDB } from "src/db/databases"
import { useAuthStore } from "src/stores/authStore"
import { useCacheStore } from "src/stores/cacheStore"
import { useUserStore } from "src/stores/userStore"
import { Log } from "src/util/classes/log"
import { CACHE_IMAGES } from "src/util/constants/configurations"
import {
  ATTACHMENT_ALLOWED_EXTENSIONS,
  ATTACHMENT_IMAGE_EXTENSIONS,
  DIR_ATTACHMENTS,
  IMG_UPLOAD_FAILED_PLACEHOLDER,
  IMG_UPLOAD_IN_PROGRESS_PLACEHOLDER,
} from "src/util/constants/files"
import { CSIurl } from "src/util/enums/urls"
import { postAttachmentForEntityBatch } from "src/util/functions/entityData"
import { showBatchErrors, showErrorNotification } from "src/util/functions/notifications"
import { showImageView } from "src/util/functions/overlays"
import { deleteIssueOnItemAttachment, getIssuesOnItem } from "src/util/http/csi/issues"
import { deleteItemAttachment, getItem } from "src/util/http/csi/items"
import {
  deleteWorkOrderCorrectiveItemAttachment,
  deleteWorkOrderRegularItemAttachment,
  getWorkOrderCorrectiveItem,
  getWorkOrderRegularItem,
} from "src/util/http/csi/workOrders"
import { ImageGalleryItem, ImageViewerItem } from "src/util/types/components/sfxImageGallery"
import {
  Attachment,
  AttachmentCard,
  AttachmentContext,
  AttachmentData,
  AttachmentUpload,
} from "src/util/types/dataModels/attachment"
import { EntityPages } from "src/util/types/dataModels/entityDefinition"
import { BatchErrors, ObjectList } from "src/util/types/dataModels/genericObject"
import { IssueTableData } from "src/util/types/dataModels/issue"
import {
  PERMISSION_ACCESS_UPDATE,
  PERMISSION_GIS,
  PERMISSION_ISSUES,
  PERMISSION_ITEMS,
  PERMISSION_PERFORMED_WORK,
  PERMISSION_WORK_ORDERS,
} from "src/util/types/dataModels/user"

/**
 * Simple check to see if the file is an image. It gets the file extension and compares it to allowed values.
 *
 * @example png
 * @param attachment
 */
export function attachmentIsImage(attachment: Attachment): boolean {
  return ATTACHMENT_IMAGE_EXTENSIONS.includes(attachment.extension)
}

/**
 * Check whether the file type is allowed for uploading
 *
 * @param extension File extension
 */
export function fileTypeIsAllowed(extension: string) {
  return ATTACHMENT_ALLOWED_EXTENSIONS.includes(extension)
}

/**
 * Splits the attachments for an entity into images and non images, and inserts them into the provided arrays.
 *
 * **CAUTION:** This mutates the original arrays provided.
 * @param entity
 * @param imageList
 * @param attachmentList
 */
export async function splitAttachments<Entity extends { attachments: string[] }>(
  entity: Entity,
  imageList: Attachment[],
  attachmentList: Attachment[]
) {
  try {
    const ids = entity.attachments

    if (ids && ids.length > 0) {
      const attachments = await getAttachments(ids)

      if (attachments.length > 0) {
        for (const attachment of attachments) {
          if (attachmentIsImage(attachment)) {
            imageList.push(attachment)
          } else {
            attachmentList.push(attachment)
          }
        }
      }
    }
  } catch (error) {
    Log.error({
      message: "An error occurred while fetching the Attachment data",
      error,
    })
  }
}

export async function getAttachments(ids: string[]) {
  const db = await getDB()
  const cacheStore = useCacheStore()
  const attachments: Attachment[] = []

  for (const id of ids) {
    const attachmentInCache = cacheStore.attachment.get(id)
    if (attachmentInCache) {
      attachments.push(attachmentInCache)
    } else {
      const attachmentInDb = await db.attachment.findOne(id).exec()
      if (attachmentInDb) {
        const attachment = attachmentInDb.toMutableJSON()
        attachments.push(attachment)
        cacheStore.attachment.set(attachment.id, attachment)
      }
    }
  }
  return attachments
}

export async function getAttachmentData(attachmentID: string): Promise<Attachment | undefined> {
  try {
    const cacheStore = useCacheStore()
    const attachmentInCache = cacheStore.attachment.get(attachmentID)

    if (attachmentInCache) {
      return attachmentInCache
    } else {
      const db = await getDB()
      const attachmentInDB = await db.attachment.findOne(attachmentID).exec()

      if (attachmentInDB) {
        const mutableAttachment = attachmentInDB.toMutableJSON()
        cacheStore.attachment.set(mutableAttachment.id, mutableAttachment)
        return mutableAttachment
      }
    }
  } catch (error) {
    Log.error({
      message: "An error occurred while fetching Item Attachment data",
      error,
    })
  }
}

export async function getAttachmentContent(
  doc: Attachment,
  entity: AttachmentContext,
  entityID: string
): Promise<string | undefined> {
  switch (doc.state) {
    // No point of fetching attachment content if the attachment is not in 'ready' state
    case 2: {
      return await getReadyAttachmentContent(doc, entity, entityID)
    }
    case -1: {
      return IMG_UPLOAD_FAILED_PLACEHOLDER
    }
    case 1:
    case 0: {
      return IMG_UPLOAD_IN_PROGRESS_PLACEHOLDER
    }
    // Default is the placeholder image set in the SfxCard.vue
  }
}

async function getReadyAttachmentContent(
  doc: Attachment,
  entity: AttachmentContext,
  entityID: string
): Promise<string | undefined> {
  const cacheStore = useCacheStore()
  const attachmentInCache = cacheStore.attachment_data.get(doc.id)
  const db = await getDB()
  let content: AttachmentData | null

  if (attachmentInCache) {
    content = attachmentInCache
  } else {
    content = await db.attachment_data.findOne(doc.id).exec()
    if (content) cacheStore.attachment_data.set(content?.id, content)
  }

  if (process.env.MODE === "capacitor") {
    return getAttachmentContentCapacitor(doc, content, entity, entityID)
  } else {
    const blobData = await getAttachmentContentWeb(doc, content, entity, entityID)
    if (blobData) return URL.createObjectURL(blobData)
  }
}

async function getAttachmentContentCapacitor(
  doc: Attachment,
  content: AttachmentData | null,
  entity: AttachmentContext,
  entityID: string
): Promise<string | undefined> {
  const { Capacitor } = await import("@src-capacitor/@capacitor/core")
  const db = await getDB()

  if (content?.path) {
    return Capacitor.convertFileSrc(content.path)
  }

  const updatedDoc = await saveAttachmentData(doc, db.attachment_data, entity, entityID)
  if (updatedDoc) return Capacitor.convertFileSrc(updatedDoc.path)
}

async function getAttachmentContentWeb(
  doc: Attachment,
  content: AttachmentData | null,
  entity: AttachmentContext,
  entityID: string
): Promise<Blob | undefined> {
  const db = await getDB()

  const cache = await caches.open(CACHE_IMAGES)

  if (content?.path) {
    const foundImage = await cache.match(content.path)
    if (foundImage) {
      return await foundImage.blob()
    }
  }

  const updatedDoc = await saveAttachmentData(doc, db.attachment_data, entity, entityID)
  if (updatedDoc) {
    const foundImage = await cache.match(updatedDoc.path)
    if (foundImage) {
      return await foundImage.blob()
    }
  }
}

/**
 * Returns a list of AttachmentUpload type images for a given list of Attachment type images
 *
 * Note: This function only works in web (Manager mode)
 *
 * @param docs
 * @param entity
 * @param entityID
 */
export async function mapAttachmentToAttachmentUpload(
  docs: Attachment[],
  entity: AttachmentContext,
  entityID: string
): Promise<AttachmentUpload[]> {
  const cacheStore = useCacheStore()
  const db = await getDB()
  const images: AttachmentUpload[] = []

  for (const doc of docs) {
    const attachmentInCache = cacheStore.attachment_data.get(doc.id)
    let content: AttachmentData | null

    if (attachmentInCache) {
      content = attachmentInCache
    } else {
      content = await db.attachment_data.findOne(doc.id).exec()
      if (content) cacheStore.attachment_data.set(content?.id, content)
    }

    const blob = await getAttachmentContentWeb(doc, content, entity, entityID)

    if (blob) {
      const image: AttachmentUpload = {
        name: doc.name,
        data: new File([blob], doc.name, { type: blob?.type }),
      }
      images.push(image)
    }
  }
  return images
}

async function saveAttachmentData(
  doc: Attachment,
  collection: AttachmentDataCollection,
  entity: AttachmentContext,
  entityID: string
): Promise<AttachmentData | undefined> {
  try {
    const { getAttachmentContentForEntity } = await import("src/util/functions/entityData")

    const fileData = await getAttachmentContentForEntity(entity, entityID, doc.id)

    if (!fileData) return

    const type = fileData.type

    if (process.env.MODE === "capacitor") {
      const { Directory } = await import(
        "@src-capacitor/@capacitor/filesystem/dist/esm/definitions"
      )
      const write_blob = await import("@src-capacitor/capacitor-blob-writer")
      const path = constructAttachmentFolderPath(doc)

      const fileDir = await write_blob.default({
        path: path,
        blob: fileData,
        directory: Directory.Data,
        recursive: true,
      })

      return await collection.upsert({ id: doc.id, type, path: fileDir })
    } else {
      const path = getWebAttachmentURL(doc.id)
      const cache = await caches.open(CACHE_IMAGES)
      await cache.put(path, new Response(fileData))
      return await collection.upsert({ id: doc.id, type, path })
    }
  } catch (error) {
    if (error instanceof CanceledError) return

    Log.error({ message: "Could not download file content", error, data: { fileName: doc.name } })
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function attachmentExists(
  doc: AttachmentDocument,
  mainFolder = DIR_ATTACHMENTS
): Promise<string | false> {
  try {
    const { Filesystem } = await import("@src-capacitor/@capacitor/filesystem")
    const { Directory } = await import("@src-capacitor/@capacitor/filesystem/dist/esm/definitions")

    const attachmentPath = constructAttachmentFolderPath(doc, mainFolder)

    const { uri } = await Filesystem.stat({ path: attachmentPath, directory: Directory.Data })
    return uri
  } catch (error) {
    if (error instanceof Error && error?.message === "File does not exist") {
      return false
    } else {
      throw error
    }
  }
}

export function getWebAttachmentURL(attachmentId: string) {
  const base = csiAPI.defaults.baseURL
  const url = CSIurl.ATTACHMENTS_ID_CONTENT.replace("{id}", attachmentId)
  return base + url
}

export function constructAttachmentFolderPath(
  attachment: AttachmentDocument | Attachment,
  mainFolder = DIR_ATTACHMENTS
) {
  // Any OS and disk format would struggle handling more than 10k files in a directory.
  // Android 11+ has changes to their system and if we have more than 3k files, it would struggle a lot.
  // This is why we split this into several folders, to lower the # of files per dir.
  // All the files should be distributed equally amongst the folders.
  // The folders should be max 256 for the first and second prefixes.

  const authStore = useAuthStore()
  const currentWorkspaceUUID = authStore.selectedWorkspace?.id

  const firstPrefix = attachment.id.slice(0, 2)
  const secondPrefix = attachment.id.slice(0, 4)

  // Split the files per workspace, so we can easily handle them
  const csiDir = currentWorkspaceUUID ? `${mainFolder}/${currentWorkspaceUUID}` : mainFolder

  return `${csiDir}/${firstPrefix}/${secondPrefix}/${attachment.id}.${attachment.extension}`
}

/**
 * Save newly added attachments in the internal app storage
 *
 * @param attachmentDocument
 * @param fileData
 */
export async function copyNewAttachmentsToAppFolder(
  attachmentDocument: AttachmentDocument[],
  fileData: Blob[]
) {
  try {
    //Should not happen, but just in case
    if (attachmentDocument.length !== fileData.length) return

    const { Directory } = await import("@src-capacitor/@capacitor/filesystem/dist/esm/definitions")
    const write_blob = await import("@src-capacitor/capacitor-blob-writer")
    let index = 0

    for (const attachment of attachmentDocument) {
      const path = constructAttachmentFolderPath(attachment)

      await write_blob.default({
        path: path,
        blob: fileData[index],
        directory: Directory.Data,
        recursive: true,
      })
      index++
    }
  } catch (error) {
    Log.error({ message: "An error occurred while copying new attachments to app folder", error })
  }
}

export async function uploadImages(
  entity: AttachmentContext,
  entityId: string,
  uploadedImages: AttachmentUpload[] | undefined
): Promise<string[] | undefined> {
  if (uploadedImages === undefined || uploadedImages.length === 0) return

  try {
    const imagesOnServer = await postAttachmentForEntityBatch(entity, entityId, uploadedImages)

    if (!imagesOnServer) return

    const successfulImages = removeBatchErrorsAndPrint(partitionBatchErrors(imagesOnServer))

    const db = await getDB()
    await db.attachment.bulkInsert(successfulImages)

    return successfulImages.map((image) => image.id)
  } catch (error) {
    const { t } = i18n.global
    const message = t("performedWork.error_saving_images")
    showErrorNotification(message)

    Log.error({
      message: "An error occurred while trying to save the images",
      error,
    })
  }
}

export async function getImageAttachmentsFromAttachmentIds(idList: string[]) {
  try {
    const attachments = await getAttachments(idList)
    const imageList: Attachment[] = []

    if (attachments.length > 0) {
      for (const attachment of attachments) {
        if (attachmentIsImage(attachment)) {
          imageList.push(attachment)
        }
      }
    }
    return imageList
  } catch (error) {
    Log.error({
      message: "An error occurred while fetching the Attachment data",
      error,
    })

    throw error
  }
}

export async function getImages(
  attachments: Attachment[],
  entity: AttachmentContext,
  entityID: string
) {
  if (attachments.length === 0) return []

  try {
    return await Promise.all(
      attachments
        .sort((a, b) => a.created_dt.localeCompare(b.created_dt))
        .map<
          Promise<ImageGalleryItem>
        >(async (doc) => await getAttachmentCardData(doc, entity, entityID))
    )
  } catch (error) {
    Log.error({
      message: "An error occurred while getting the image data.",
      error,
    })

    throw error
  }
}

export async function getImageViewerItems(
  entity: AttachmentContext,
  entityId: string,
  attachments?: Attachment[]
): Promise<ImageViewerItem[] | undefined> {
  if (!attachments) return []

  try {
    return await Promise.all(
      attachments
        .sort((a, b) => a.created_dt.localeCompare(b.created_dt))
        .map<
          Promise<ImageViewerItem>
        >(async (doc) => await getAttachmentCardData(doc, entity, entityId))
    )
  } catch (error) {
    Log.error({
      message: "An error occurred while getting the image viewer items data.",
      error,
    })
  }
}

export async function getAttachmentCardData(
  attachment: Attachment,
  entity: AttachmentContext,
  entityID: string
): Promise<AttachmentCard> {
  return {
    attachmentData: attachment,
    src: (await getAttachmentContent(attachment, entity, entityID)) || "",
  }
}

export async function getFilesystemPermission() {
  try {
    let permissions = await Filesystem.checkPermissions()
    if (permissions.publicStorage !== "granted") {
      permissions = await Filesystem.requestPermissions()
    }
    return permissions.publicStorage === "granted"
  } catch (error) {
    Log.error({ message: "Failed to get the file system permission in android", error })
  }
}

export async function getEntityCardImage(
  attachmentIdList: string[],
  entity: AttachmentContext,
  entityID: string
) {
  try {
    const attachments = await getAttachments(attachmentIdList)

    if (attachments.length > 0) {
      for (const attachment of attachments) {
        if (attachmentIsImage(attachment)) {
          return await getAttachmentContent(attachment, entity, entityID)
        }
      }
    }
  } catch (error) {
    Log.error({
      message: "An error occurred while fetching the Entity image data",
      error,
    })
  }
}

export async function getFirstImageAttachment(attachmentIdList: string[]) {
  try {
    const attachments = await getAttachments(attachmentIdList)

    if (attachments.length > 0) {
      for (const attachment of attachments) {
        if (attachmentIsImage(attachment)) {
          return attachment
        }
      }
    }
  } catch (error) {
    if (error instanceof CanceledError) return

    Log.error({
      message: "An error occurred while fetching the first image attachment",
      error,
    })
  }
}

/**
 * Return an image attachment for a given image attachmentId
 *
 * @param attachmentId image attachmentId
 */
export async function getImageAttachment(attachmentId: string): Promise<Attachment | undefined> {
  try {
    const attachment = await getAttachmentData(attachmentId)

    if (attachment) return attachment
  } catch (error) {
    if (error instanceof CanceledError) return

    Log.error({
      message: "An error occurred while fetching the image attachment",
      error,
    })
  }
}

/**
 * The selected attachment will be handled based on the type and/or environment
 * if (image) -> shown in the image view dialog
 * else if (other file types and capacitor env) -> shown in the FileOpener in capacitor
 * else (in a web browser) -> download the file
 *
 * @param attachment
 * @param entity
 * @param entityID
 */
export async function showOrDownloadAttachment(
  attachment: Attachment,
  entity: AttachmentContext,
  entityID: string
) {
  try {
    const attachmentCard = await getAttachmentCardData(attachment, entity, entityID)

    if (attachmentIsImage(attachment)) {
      showImageView(attachmentCard, [attachmentCard])
    } else if (Platform.is.capacitor) {
      const absolutePath = new URL(attachmentCard?.src).pathname
      const filePath = absolutePath.split("_capacitor_file_")[1]

      return await FileOpener.open({ filePath: filePath })
    } else {
      //Download the file in the browser
      return axios
        .get(attachmentCard?.src, { responseType: "blob" })
        .then((response: { data: BlobPart }) => {
          const blob = new Blob([response.data])
          const link = document.createElement("a")
          link.href = URL.createObjectURL(blob)
          link.download = attachmentCard.attachmentData.name
          link.click()
          URL.revokeObjectURL(link.href)
        })
    }
  } catch (error) {
    Log.error({ message: "Failed to fetch the file in SfxCardAttachment", error })
  }
}

export function partitionBatchErrors<SuccessEntity extends object>(
  list: ObjectList<SuccessEntity | BatchErrors>
): [SuccessEntity[], BatchErrors[]] {
  return list.results.reduce(
    (array, element) => {
      "error" in element ? array[1].push(element) : array[0].push(element)
      return array
    },
    [[], []] as [SuccessEntity[], BatchErrors[]]
  )
}

export function removeBatchErrorsAndPrint<SuccessEntity>(
  list: [SuccessEntity[], BatchErrors[]]
): SuccessEntity[] {
  if (list[1].length > 0) {
    showBatchErrors(list[1], list[0].length > 0)
  }
  return list[0]
}

/**
 * Returns the correct attachment context based on the provided entity.
 * Returns null in case it cannot determine the context.
 *
 * @param entityPage
 * @param entity
 */
export function getAttachmentContextFromEntityPage(
  entityPage: EntityPages,
  entity: Record<string, unknown>
): AttachmentContext | null {
  // TODO: Think of some better way to deal with this in the SfxTable attachment content fetch. This is just sad.
  switch (entityPage) {
    case "area":
      return null
    case "attachment":
      return null
    case "workspace":
      return null
    case "layout":
      return null
    case "work_order":
      // TODO: 😭
      //  Work orders don't have image attachments, they have graphs
      return null
    case "zone":
      return null
    case "item":
      return "item"
    case "model":
      return "item_model"
    case "location":
      return null
    case "issue":
      return "actionable_item" in (entity as IssueTableData).issueData ? "issue_item" : "issue_area"
    case "performed_work": {
      if (!("isCorrectiveWork" in entity)) {
        throw new Error("Schema for performed work is incorrect")
      }

      if (entity.isCorrectiveWork) {
        return "corrective_work_item"
      } else {
        return "performedWorkOnItemData" in entity ? "regular_work_item" : "regular_work_area"
      }
    }
  }
}

/**
 * Returns the attachment id list for an entity
 *
 * @param entity
 * @param entityId
 */
export async function getEntityAttachmentIdList(
  entity: AttachmentContext,
  entityId: string
): Promise<string[] | undefined> {
  try {
    const db = await getDB()

    switch (entity) {
      case "item": {
        const itemInDb = await db.item.findOne(entityId).exec()
        return itemInDb?.attachments
      }

      case "item_model": {
        const itemModelInDb = await db.item_model.findOne(entityId).exec()
        return itemModelInDb?.attachments
      }

      case "issue_item": {
        const issueInDb = await db.issues_on_item.findOne(entityId).exec()
        return issueInDb?.attachments
      }

      case "issue_item_comment": {
        const issueItemCommentInDb = await db.issue_on_item_comments.findOne(entityId).exec()
        return issueItemCommentInDb?.attachments
      }

      case "corrective_work_item": {
        const correctiveWorkItemInDb = await db.corrective_work_on_item.findOne(entityId).exec()
        return correctiveWorkItemInDb?.attachments
      }

      case "regular_work_item": {
        const regularWorkItemInDb = await db.regular_work_on_item.findOne(entityId).exec()
        return regularWorkItemInDb?.attachments
      }

      case "work_order_corrective_item": {
        const workOrderCorrectiveItemInDb = await db.work_order_corrective_item
          .findOne(entityId)
          .exec()
        return workOrderCorrectiveItemInDb?.attachments
      }

      case "work_order_regular_item": {
        const workOrderRegularItemInDb = await db.work_order_regular_item.findOne(entityId).exec()
        return workOrderRegularItemInDb?.attachments
      }

      case "work_order_corrective_item_comment": {
        const workOrderCorrectiveItemCommentInDb = await db.work_order_corrective_comments
          .findOne(entityId)
          .exec()
        return workOrderCorrectiveItemCommentInDb?.attachments
      }

      case "work_order_regular_item_comment": {
        const workOrderRegularItemCommentInDb = await db.work_order_regular_comments
          .findOne(entityId)
          .exec()
        return workOrderRegularItemCommentInDb?.attachments
      }

      case "corrective_work_area":
      case "regular_work_area":
      case "issue_area":
      case "work_order_corrective_area":
      case "work_order_regular_area":
      case "work_order_corrective_area_comment":
      case "work_order_regular_area_comment":
      case "area":
      case "zone":
        return
    }
  } catch (error) {
    Log.error({ message: "Failed to get the entity attachment id list", error })
  }
}

/**
 * Update the entity in the UI
 *
 * @param entity
 * @param entityId
 */
export async function updateEntityInUI(entity: AttachmentContext, entityId: string) {
  try {
    const db = await getDB()

    switch (entity) {
      case "item": {
        const updatedItemInServer = await getItem({}, entityId)
        await db.item.upsert(updatedItemInServer)

        break
      }
      case "issue_item": {
        const updatedIssueInServer = await getIssuesOnItem({}, entityId)
        await db.issues_on_item.upsert(updatedIssueInServer)

        break
      }
      case "work_order_corrective_item": {
        const updatedWorkOrderInServer = await getWorkOrderCorrectiveItem({}, entityId)
        await db.work_order_corrective_item.upsert(updatedWorkOrderInServer)

        break
      }
      case "work_order_regular_item": {
        const updatedWorkOrderInServer = await getWorkOrderRegularItem({}, entityId)
        await db.work_order_regular_item.upsert(updatedWorkOrderInServer)

        break
      }
    }
  } catch (error) {
    Log.error({ message: "Failed to update the entity attachment id list", error })
  }
}

/**
 * Delete the attachment IDs from the given entity and return the response statuses from axios
 *
 * @param attachmentIds
 * @param entity
 * @param entityId
 */
export async function deleteEntityAttachments(
  attachmentIds: string[],
  entity: AttachmentContext,
  entityId: string
) {
  try {
    // Note: There is no need to handle the comments of entities, as they cannot be edited

    const deleteAPICalls = []

    switch (entity) {
      case "item": {
        for (const attachmentId of attachmentIds) {
          deleteAPICalls.push(deleteItemAttachment({}, entityId, attachmentId))
        }
        break
      }

      case "issue_item": {
        for (const attachmentId of attachmentIds) {
          deleteAPICalls.push(deleteIssueOnItemAttachment({}, entityId, attachmentId))
        }
        break
      }

      case "work_order_corrective_item": {
        for (const attachmentId of attachmentIds) {
          deleteAPICalls.push(deleteWorkOrderCorrectiveItemAttachment({}, entityId, attachmentId))
        }
        break
      }

      case "work_order_regular_item": {
        for (const attachmentId of attachmentIds) {
          deleteAPICalls.push(deleteWorkOrderRegularItemAttachment({}, entityId, attachmentId))
        }
        break
      }
    }

    const APIResponses = await Promise.all(deleteAPICalls)
    await updateEntityInUI(entity, entityId)

    return APIResponses.map((response, index) => {
      return { attachmentId: attachmentIds[index], status: response.status }
    })
  } catch (error) {
    Log.error({ message: "Failed to delete the attachments from the entity", error })
  }
}

export function canUpdateEntityAttachments(entity: AttachmentContext): boolean {
  const userStore = useUserStore()
  let canEdit = true

  switch (entity) {
    case "item":
    case "item_model": {
      canEdit = userStore.checkUserPermission(PERMISSION_ITEMS, PERMISSION_ACCESS_UPDATE)
      break
    }

    case "issue_item":
    case "issue_area":
    case "issue_item_comment": {
      canEdit = userStore.checkUserPermission(PERMISSION_ISSUES, PERMISSION_ACCESS_UPDATE)
      break
    }

    case "corrective_work_area":
    case "corrective_work_item":
    case "corrective_work_item_comment":
    case "regular_work_area":
    case "regular_work_item":
    case "regular_work_item_comment": {
      canEdit = userStore.checkUserPermission(PERMISSION_PERFORMED_WORK, PERMISSION_ACCESS_UPDATE)
      break
    }

    case "area":
    case "zone": {
      canEdit = userStore.checkUserPermission(PERMISSION_GIS, PERMISSION_ACCESS_UPDATE)
      break
    }

    case "work_order_corrective_item":
    case "work_order_corrective_area":
    case "work_order_corrective_item_comment":
    case "work_order_corrective_area_comment":
    case "work_order_regular_item":
    case "work_order_regular_area":
    case "work_order_regular_item_comment":
    case "work_order_regular_area_comment": {
      canEdit = userStore.checkUserPermission(PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_UPDATE)
      break
    }
  }

  return canEdit
}
